import React, { useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Tag } from 'antd';
import { AlertOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchSqlData } from '../../../services/api';
import { useTranslation } from 'react-i18next';
import StandardTable, { createActionColumn } from '../../../components/common/StandardTable';
import FilterBar from '../../../components/common/FilterBar';
import type { AdmVehicle } from '../../../types';

const { Title } = Typography;

interface MaintAlert {
  id: number;
  vehicle_id: number;
  maintenance_type_id: number;
  alert_status?: 'Approaching' | 'Due' | 'Overdue' | string | null;
  due_date?: string | null;
  due_km?: number | null;
  due_hours?: number | null;
  notes?: string | null;
  created_at?: string | null;
}
interface MaintType { id: number; name: string; }

const getAlertStatusMeta = (status: string | null | undefined, t: (key: string) => string) => {
  switch (status) {
    case 'Approaching':
      return { color: 'warning', label: t('machinery.maintenance.alerts.approaching') };
    case 'Due':
      return { color: 'error', label: t('machinery.maintenance.alerts.due') };
    case 'Overdue':
      return { color: 'magenta', label: t('machinery.maintenance.alerts.overdue') };
    default:
      return { color: 'default', label: status || '-' };
  }
};

const MachMaintenanceAlerts: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === 'tr' ? 'tr-TR' : 'en-US';
  const navigate = useNavigate();
  const { data: alerts = [], isLoading } = useQuery<MaintAlert[], Error>({
    queryKey: ['adm_maintenance_alerts'],
    queryFn: () => fetchSqlData('adm_maintenance_alerts'),
    staleTime: 60_000,
  });
  const { data: vehicles = [] } = useQuery<AdmVehicle[], Error>({
    queryKey: ['adm_vehicles_for_alerts'],
    queryFn: () => fetchSqlData('adm_vehicles'),
    staleTime: 5 * 60_000,
  });
  const { data: types = [] } = useQuery<MaintType[], Error>({
    queryKey: ['adm_maintenance_types'],
    queryFn: () => fetchSqlData('adm_maintenance_types'),
    staleTime: 10 * 60_000,
  });

  const vehiclesMap = useMemo(() => {
    const m: Record<number, string> = {};
    (vehicles || []).forEach(v => {
      if (v.id) {
        m[v.id] = `${v.license_plate || '-'} ${v.make || ''} ${v.model || ''}`.trim();
      }
    });
    return m;
  }, [vehicles]);
  const typeMap = useMemo(() => {
    const m: Record<number, string> = {};
    (types || []).forEach(t => { if (t.id) m[t.id] = t.name; });
    return m;
  }, [types]);

  const [vehicleFilter, setVehicleFilter] = useState<number | undefined>(undefined);
  const [typeFilter, setTypeFilter] = useState<number | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');

  const filtered = useMemo(() => {
    let arr = alerts;
    if (vehicleFilter != null) arr = arr.filter(a => Number(a.vehicle_id) === Number(vehicleFilter));
    if (typeFilter != null) arr = arr.filter(a => Number(a.maintenance_type_id) === Number(typeFilter));
    if (statusFilter) arr = arr.filter(a => (a.alert_status || '') === statusFilter);
    if (searchTerm.trim()) {
      const s = searchTerm.trim().toLowerCase();
      arr = arr.filter(a =>
        (vehiclesMap[a.vehicle_id] || '').toLowerCase().includes(s) ||
        (typeMap[a.maintenance_type_id] || '').toLowerCase().includes(s) ||
        (a.notes || '').toLowerCase().includes(s) ||
        (getAlertStatusMeta(a.alert_status, t).label || '').toLowerCase().includes(s)
      );
    }
    return arr;
  }, [alerts, vehicleFilter, typeFilter, statusFilter, searchTerm, vehiclesMap, typeMap, t]);

  const columns = [
    { title: t('machinery.maintenance.common.vehicle'), dataIndex: 'vehicle_id', key: 'vehicle_id', render: (id: number) => vehiclesMap[id] || id, width: 220,
      sorter: (a: MaintAlert, b: MaintAlert) => (vehiclesMap[a.vehicle_id] || '').localeCompare(vehiclesMap[b.vehicle_id] || '') },
    { title: t('machinery.maintenance.common.maintenanceType'), dataIndex: 'maintenance_type_id', key: 'maintenance_type_id', render: (id: number) => typeMap[id] || id, width: 220,
      sorter: (a: MaintAlert, b: MaintAlert) => (typeMap[a.maintenance_type_id] || '').localeCompare(typeMap[b.maintenance_type_id] || '') },
    { title: t('machinery.maintenance.common.status'), dataIndex: 'alert_status', key: 'alert_status', width: 150,
      render: (status?: string | null) => {
        const meta = getAlertStatusMeta(status, t);
        return <Tag color={meta.color}>{meta.label}</Tag>;
      } },
    { title: t('machinery.maintenance.alerts.dueDate'), dataIndex: 'due_date', key: 'due_date', width: 160, render: (d?: string | null) => d ? new Date(d).toLocaleDateString(locale) : '-' },
    { title: t('machinery.maintenance.alerts.dueKm'), dataIndex: 'due_km', key: 'due_km', align: 'right' as const, width: 120, render: (v?: number | null) => v?.toLocaleString(locale) || '-' },
    { title: t('machinery.maintenance.alerts.dueHours'), dataIndex: 'due_hours', key: 'due_hours', align: 'right' as const, width: 120, render: (v?: number | null) => v?.toLocaleString(locale) || '-' },
    { title: t('machinery.maintenance.alerts.note'), dataIndex: 'notes', key: 'notes' },
  ];

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <AlertOutlined style={{ marginRight: 8 }} />
            {t('machinery.maintenance.alerts.title')}
          </Title>
        </Col>
      </Row>

      <FilterBar
        items={[
          { type: 'text', key: 'search', placeholder: t('machinery.maintenance.alerts.searchPlaceholder'), value: searchTerm, onChange: v => setSearchTerm(v), width: 280 },
          { type: 'select', key: 'vehicle', placeholder: t('machinery.maintenance.alerts.vehiclePlaceholder'), value: vehicleFilter as any, onChange: v => setVehicleFilter(v),
            options: (vehicles || []).map(v => ({ value: v.id, label: vehiclesMap[v.id] || v.license_plate || String(v.id) })), width: 280, allowClear: true, showSearch: true },
          { type: 'select', key: 'type', placeholder: t('machinery.maintenance.alerts.typePlaceholder'), value: typeFilter as any, onChange: v => setTypeFilter(v),
            options: (types || []).map(t => ({ value: t.id, label: t.name })), width: 220, allowClear: true, showSearch: true },
          { type: 'select', key: 'status', placeholder: t('machinery.maintenance.alerts.statusPlaceholder'), value: statusFilter as any, onChange: v => setStatusFilter(v),
            options: [
              { value: 'Approaching', label: t('machinery.maintenance.alerts.approaching') },
              { value: 'Due', label: t('machinery.maintenance.alerts.due') },
              { value: 'Overdue', label: t('machinery.maintenance.alerts.overdue') },
            ], width: 180, allowClear: true },
        ]}
        onReset={() => { setSearchTerm(''); setVehicleFilter(undefined); setTypeFilter(undefined); setStatusFilter(undefined); }}
      />

      <StandardTable
        rowKey="id"
        columns={[
          ...columns,
          createActionColumn<MaintAlert>({
            onView: (record) => navigate(`/machinery/vehicles/show/${record.vehicle_id}`),
            showEdit: false,
            showDelete: false,
            align: 'left',
          }) as any,
        ]}
        dataSource={filtered}
        loading={isLoading}
        bordered
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />
    </Card>
  );
};

export default MachMaintenanceAlerts;

